import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import type { SupportedCurrency, SubscriptionDetails } from '@plevits/types';
import { billing } from '../lib/api';
import { trackStripeCheckoutStarted } from '../lib/analytics';
import { usePremiumAccess } from './usePremiumAccess';
import { useAuth } from './useAuth';

const POLL_INTERVAL_BASE = 2000;
const POLL_JITTER = 200;
const POLL_TIMEOUT = 30_000;

function jitteredInterval(): number {
  return POLL_INTERVAL_BASE + Math.random() * POLL_JITTER * 2 - POLL_JITTER;
}

export function useBilling() {
  const queryClient = useQueryClient();
  const { isPaidPro, tempUnlocked } = usePremiumAccess();
  const { user } = useAuth();
  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const billingStatus = useQuery({
    queryKey: ['billing', 'status'],
    queryFn: () => billing.getStatus(),
    staleTime: 30_000,
    enabled: !!user,
  });

  const subscriptionDetails = useQuery({
    queryKey: ['billing', 'subscription-details'],
    queryFn: () => billing.getSubscriptionDetails(),
    staleTime: 30_000,
    enabled: !!user && (isPaidPro || user.plan === 'enterprise'),
  });

  const invoices = useQuery({
    queryKey: ['billing', 'invoices'],
    queryFn: () => billing.getInvoices(),
    enabled: false,
  });

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  // Fields that are DB-entitlement-observable and need Phase 2 convergence
  const DB_CONVERGENCE_FIELDS: Array<keyof SubscriptionDetails> = ['cancelAtPeriodEnd'];
  const DB_CONVERGENCE_TIMEOUT = 15_000;

  const startPolling = useCallback(
    <K extends keyof SubscriptionDetails>(
      field: K,
      expectedValue: SubscriptionDetails[K],
      onConverged: () => void,
      onTimeout: () => void,
    ) => {
      stopPolling();
      const startTime = Date.now();

      // Phase 2: Poll DB-backed billing/status for entitlement convergence
      const startDbConvergence = () => {
        const dbStartTime = Date.now();

        const pollDb = async () => {
          if (Date.now() - dbStartTime > DB_CONVERGENCE_TIMEOUT) {
            // DB convergence timed out but Stripe already converged — still succeed
            queryClient.invalidateQueries({ queryKey: ['billing'] });
            onConverged();
            return;
          }

          try {
            const statusData = await queryClient.fetchQuery({
              queryKey: ['billing', 'status'],
              queryFn: () => billing.getStatus(),
              staleTime: 0,
            });

            if ((statusData as Record<string, unknown>)[field as string] === expectedValue) {
              queryClient.invalidateQueries({ queryKey: ['billing'] });
              onConverged();
              return;
            }
          } catch {
            // continue polling
          }

          pollingRef.current = setTimeout(pollDb, jitteredInterval());
        };

        pollingRef.current = setTimeout(pollDb, jitteredInterval());
      };

      // Phase 1: Poll Stripe-backed subscription-details
      const poll = async () => {
        if (Date.now() - startTime > POLL_TIMEOUT) {
          onTimeout();
          return;
        }

        try {
          const data = await queryClient.fetchQuery({
            queryKey: ['billing', 'subscription-details'],
            queryFn: () => billing.getSubscriptionDetails(),
            staleTime: 0,
          });

          if (data[field] === expectedValue) {
            // Phase 1 converged — check if Phase 2 needed
            if (DB_CONVERGENCE_FIELDS.includes(field)) {
              startDbConvergence();
              return;
            }
            // No Phase 2 needed (e.g., billingPeriod is display-only)
            queryClient.invalidateQueries({ queryKey: ['billing'] });
            onConverged();
            return;
          }
        } catch {
          // continue polling
        }

        pollingRef.current = setTimeout(poll, jitteredInterval());
      };

      pollingRef.current = setTimeout(poll, jitteredInterval());
    },
    [queryClient, stopPolling], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const startPaymentMethodPolling = useCallback(
    (originalPmId: string | undefined, onConverged: () => void, onTimeout: () => void) => {
      stopPolling();
      const startTime = Date.now();

      const poll = async () => {
        if (Date.now() - startTime > POLL_TIMEOUT) {
          onTimeout();
          return;
        }

        try {
          const data = await queryClient.fetchQuery({
            queryKey: ['billing', 'subscription-details'],
            queryFn: () => billing.getSubscriptionDetails(),
            staleTime: 0,
          });

          if (data.paymentMethod?.paymentMethodId && data.paymentMethod.paymentMethodId !== originalPmId) {
            queryClient.invalidateQueries({ queryKey: ['billing'] });
            onConverged();
            return;
          }
        } catch {
          // continue polling
        }

        pollingRef.current = setTimeout(poll, jitteredInterval());
      };

      pollingRef.current = setTimeout(poll, jitteredInterval());
    },
    [queryClient, stopPolling],
  );

  const createCheckout = useMutation({
    mutationFn: ({ billingPeriod, currency, source = 'billing_checkout' }: { billingPeriod: 'monthly' | 'annual'; currency: SupportedCurrency; source?: 'pricing' | 'billing_modal_upgrade' | 'billing_checkout' }) => {
      trackStripeCheckoutStarted(source, { isPaidPro, tempUnlocked });
      return billing.createCheckoutSession(billingPeriod, currency);
    },
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });

  const openPortal = useMutation({
    mutationFn: () => billing.createPortalSession(),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });

  const cancelSubscription = useMutation({
    mutationFn: () => billing.cancelSubscription(),
  });

  const reactivateSubscription = useMutation({
    mutationFn: () => billing.reactivateSubscription(),
  });

  const updateBillingPeriod = useMutation({
    mutationFn: (billingPeriod: 'monthly' | 'annual') => billing.updateBillingPeriod(billingPeriod),
  });

  const createSetupIntent = useMutation({
    mutationFn: () => billing.createSetupIntent(),
  });

  const invalidateStatus = () => {
    queryClient.invalidateQueries({ queryKey: ['billing', 'status'] });
  };

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['billing'] });
  };

  return {
    billingStatus,
    subscriptionDetails,
    invoices,
    createCheckout,
    openPortal,
    cancelSubscription,
    reactivateSubscription,
    updateBillingPeriod,
    createSetupIntent,
    invalidateStatus,
    invalidateAll,
    startPolling,
    startPaymentMethodPolling,
    stopPolling,
  };
}
